<script lang="ts" setup>
import { ref, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useEventListener } from '@vueuse/core'
import { useGlobalSearch, type SearchResult } from '@/composables/useGlobalSearch'

defineOptions({ name: 'GlobalSearchModal' })

const router = useRouter()
const search = useGlobalSearch()

const inputRef = ref<HTMLInputElement | null>(null)
const modalRef = ref<HTMLElement | null>(null)

// Focus input when opened
async function focusInput(): Promise<void> {
  await nextTick()
  inputRef.value?.focus()
}

// Handle result selection
function selectResult(result: SearchResult): void {
  result.action?.()
  if (result.route) {
    search.saveRecentSearch(search.query.value)
    router.push(result.route)
  }
  search.close()
}

// Handle Enter key
function handleEnter(): void {
  const result = search.getSelectedResult()
  if (result) {
    selectResult(result)
  }
}

if (typeof window !== 'undefined') {
  // Global keyboard shortcut: Cmd/Ctrl + K
  useEventListener(window, 'keydown', (e: KeyboardEvent) => {
    const isCmdK = (e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === 'k'
    if (isCmdK) {
      e.preventDefault()
      search.toggle()
      if (search.isOpen.value) {
        focusInput()
      }
    }
  })
}

// Focus input when modal opens
onMounted(() => {
  if (search.isOpen.value) {
    focusInput()
  }
})

// Get category icon
function getCategoryIcon(category: string): string {
  switch (category) {
    case 'domain': return 'mdi-web'
    case 'navigation': return 'mdi-compass'
    case 'product': return 'mdi-package-variant'
    default: return 'mdi-magnify'
  }
}
</script>

<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="backdrop">
      <div v-if="search.isOpen.value" class="search-backdrop" @click="search.close" />
    </Transition>

    <!-- Modal -->
    <Transition name="search-modal" @after-enter="focusInput">
      <div v-if="search.isOpen.value" ref="modalRef" class="search-modal">
        <!-- Search Input -->
        <div class="search-input-wrapper">
          <v-icon icon="mdi-magnify" size="22" class="search-icon" />
          <input
            ref="inputRef"
            v-model="search.query.value"
            type="text"
            class="search-input"
            :placeholder="$t('globalSearch.placeholder')"
            autocomplete="off"
            spellcheck="false"
            @keydown="search.handleKeyDown"
            @keydown.enter.prevent="handleEnter"
          />
          <button
            v-if="search.query.value"
            type="button"
            class="clear-btn"
            :aria-label="$t('globalSearch.clearInput')"
            @click="search.query.value = ''"
          >
            <v-icon icon="mdi-close" size="18" />
          </button>
          <button
            type="button"
            class="close-btn"
            :aria-label="$t('globalSearch.closeSearch')"
            @click="search.close"
          >
            <kbd>esc</kbd>
          </button>
        </div>

        <!-- Results -->
        <div class="search-results">
          <!-- Loading State -->
          <div v-if="search.isLoading.value" class="search-loading">
            <v-progress-circular indeterminate size="24" width="2" color="primary" />
          </div>

          <!-- Empty State -->
          <div v-else-if="search.query.value && search.results.value.length === 0" class="search-empty">
            <v-icon icon="mdi-magnify-close" size="32" class="empty-icon" />
            <p>{{ $t('globalSearch.noResults', { query: search.query.value }) }}</p>
          </div>

          <!-- Grouped Results -->
          <template v-else>
            <div
              v-for="group in search.groupedResults.value"
              :key="group.category"
              class="result-group"
            >
              <div class="group-header">
                <v-icon :icon="getCategoryIcon(group.category)" size="16" />
                <span>{{ group.label }}</span>
              </div>
              <div class="group-items">
                <button
                  v-for="result in group.items"
                  :key="result.id"
                  type="button"
                  class="result-item"
                  :class="{ 'is-selected': search.results.value.indexOf(result) === search.selectedIndex.value }"
                  @click="selectResult(result)"
                  @mouseenter="search.selectedIndex.value = search.results.value.indexOf(result)"
                >
                  <div class="result-icon">
                    <v-icon :icon="result.icon" size="20" />
                  </div>
                  <div class="result-content">
                    <span class="result-title">{{ result.title }}</span>
                    <span v-if="result.subtitle" class="result-subtitle">{{ result.subtitle }}</span>
                  </div>
                  <v-icon icon="mdi-arrow-right" size="16" class="result-arrow" />
                </button>
              </div>
            </div>
          </template>

          <!-- Recent Searches -->
          <div v-if="!search.query.value && search.recentSearches.value.length > 0" class="recent-searches">
            <div class="recent-header">
              <span>{{ $t('globalSearch.recentSearches') }}</span>
              <button type="button" class="clear-recent" @click="search.clearRecentSearches">
                {{ $t('globalSearch.clearRecent') }}
              </button>
            </div>
            <div class="recent-items">
              <button
                v-for="recent in search.recentSearches.value"
                :key="recent"
                type="button"
                class="recent-item"
                @click="search.query.value = recent"
              >
                <v-icon icon="mdi-history" size="16" />
                <span>{{ recent }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="search-footer">
          <div class="footer-hint">
            <kbd>↑</kbd><kbd>↓</kbd> {{ $t('globalSearch.hints.navigate') }}
            <kbd>↵</kbd> {{ $t('globalSearch.hints.select') }}
            <kbd>esc</kbd> {{ $t('globalSearch.hints.close') }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
/* Backdrop */
.search-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 9998;
}

/* Modal */
.search-modal {
  position: fixed;
  top: 15%;
  left: 50%;
  transform: translateX(-50%);
  width: 580px;
  max-width: calc(100vw - 32px);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 20px;
  box-shadow:
    0 25px 60px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  z-index: 9999;
  overflow: hidden;
}

/* Search Input */
.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.search-icon {
  color: rgba(120, 163, 255, 0.6);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.95);

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.06);
  border: none;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.8);
  }
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.15s ease;

  kbd {
    font-family: inherit;
    font-size: 0.75rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
  }
}

/* Search Results */
.search-results {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.search-loading,
.search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 20px;
  color: rgba(200, 218, 255, 0.5);
}

.empty-icon {
  opacity: 0.4;
}

/* Result Groups */
.result-group {
  &:not(:last-child) {
    margin-bottom: 10px;
  }
}

.group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(120, 163, 255, 0.7);
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Result Item */
.result-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px 14px;
  background: transparent;
  border: none;
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover,
  &.is-selected {
    background: rgba(120, 163, 255, 0.1);
  }

  &.is-selected {
    .result-arrow {
      opacity: 1;
      transform: translateX(0);
    }
  }
}

.result-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(0, 176, 255, 0.12), rgba(31, 226, 212, 0.08));
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 10px;
  color: #00B0FF;
  flex-shrink: 0;
}

.result-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.result-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-subtitle {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-arrow {
  color: rgba(120, 163, 255, 0.5);
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.15s ease;
}

/* Recent Searches */
.recent-searches {
  padding: 10px 0;
}

.recent-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.clear-recent {
  background: none;
  border: none;
  color: rgba(120, 163, 255, 0.7);
  font-size: 0.75rem;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #00B0FF;
  }
}

.recent-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 14px;
}

.recent-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 8px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(120, 163, 255, 0.2);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Search Footer */
.search-footer {
  padding: 14px 18px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.footer-hint {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.4);

  kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 5px;
    font-family: inherit;
    font-size: 0.7rem;
  }
}

/* Transitions */
.search-modal-enter-active {
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.search-modal-leave-active {
  transition: all 0.15s ease;
}

.search-modal-enter-from,
.search-modal-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-16px) scale(0.96);
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

/* Scrollbar */
.search-results {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(120, 163, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(120, 163, 255, 0.3);
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .search-modal {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(28, 110, 227, 0.15);
  }

  .search-input-wrapper {
    border-bottom-color: rgba(28, 110, 227, 0.1);
  }

  .search-icon {
    color: rgba(28, 110, 227, 0.6);
  }

  .search-input {
    color: rgba(15, 23, 42, 0.95);

    &::placeholder {
      color: rgba(15, 23, 42, 0.4);
    }
  }

  .clear-btn,
  .close-btn {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.1);
    color: rgba(15, 23, 42, 0.5);

    &:hover {
      background: rgba(0, 0, 0, 0.08);
      color: rgba(15, 23, 42, 0.8);
    }
  }

  .search-loading,
  .search-empty {
    color: rgba(15, 23, 42, 0.5);
  }

  .group-header {
    color: rgba(28, 110, 227, 0.7);
  }

  .result-item {
    &:hover,
    &.is-selected {
      background: rgba(28, 110, 227, 0.08);
    }
  }

  .result-title {
    color: rgba(15, 23, 42, 0.92);
  }

  .result-subtitle {
    color: rgba(15, 23, 42, 0.5);
  }

  .result-arrow {
    color: rgba(28, 110, 227, 0.5);
  }

  .recent-header {
    color: rgba(15, 23, 42, 0.5);
  }

  .clear-recent {
    color: rgba(28, 110, 227, 0.7);

    &:hover {
      color: var(--token-color-primary);
    }
  }

  .recent-item {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(28, 110, 227, 0.1);
    color: rgba(15, 23, 42, 0.7);

    &:hover {
      background: rgba(0, 0, 0, 0.08);
      border-color: rgba(28, 110, 227, 0.2);
      color: rgba(15, 23, 42, 0.9);
    }
  }

  .search-footer {
    border-top-color: rgba(28, 110, 227, 0.1);
  }

  .footer-hint {
    color: rgba(15, 23, 42, 0.4);

    kbd {
      background: rgba(0, 0, 0, 0.04);
      border-color: rgba(0, 0, 0, 0.1);
    }
  }

  .search-backdrop {
    background: rgba(0, 0, 0, 0.4);
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .search-modal,
  .result-item,
  .result-arrow,
  .clear-btn,
  .close-btn,
  .recent-item,
  .clear-recent {
    transition: none;
  }

  .search-modal-enter-active,
  .search-modal-leave-active,
  .backdrop-enter-active,
  .backdrop-leave-active {
    transition: none;
  }
}

/* Responsive */
@media (max-width: 600px) {
  .search-modal {
    top: 10%;
    width: auto;
    left: 16px;
    right: 16px;
    transform: none;
    max-height: 80vh;
  }

  .search-modal-enter-from,
  .search-modal-leave-to {
    transform: translateY(-16px) scale(0.96);
  }

  .search-input-wrapper {
    padding: 16px;
  }

  .search-input {
    font-size: 1rem;
  }
}
</style>
